'use client';

import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useScroll, useMotionValueEvent } from 'framer-motion';

import { useFramePreloader } from '@/hooks/useFramePreloader';

type Props = {
  basePath: string;
  startFrame: number;
  endFrame: number;
  scrollLengthRem?: number;
  fit?: 'cover' | 'contain';
  children?: React.ReactNode;
};

/** Clamps a number between min and max values */
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

// Performance constants
const LERP_FACTOR = 0.9; // Smoothness: 0.05 (very smooth) to 0.3 (responsive)
const IDLE_FRAMES_THRESHOLD = 20; // Frames before RAF stops
const NEARBY_RADIUS = 80; // Default search radius for nearest loaded frame
const FALLBACK_RADIUS = 140; // Extended search radius for fallback
const PRELOAD_HINT_RADIUS = 60; // Frames to hint around current position
const LERP_THRESHOLD = 0.01; // Minimum difference before considering idle
const TABLE_REBUILD_INTERVAL = 150; // ms between rebuild checks
const MAX_DEVICE_PIXEL_RATIO = 2; // Cap DPR to avoid excessive memory usage

export default function ScrollVideoFramesCanvas({
  basePath,
  startFrame,
  endFrame,
  scrollLengthRem = 600,
  fit = 'cover',
  children,
}: Props) {
  const total = endFrame - startFrame + 1;

  const sectionRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const rectRef = useRef({ x: 0, y: 0, w: 0, h: 0 });

  // Use framer-motion's optimized useScroll hook
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end end'],
  });

  const scrollTargetRef = useRef(0);
  const currentIdxRef = useRef(0);
  const lastDrawnRef = useRef<number | null>(null);

  // Smooth interpolation value
  const smoothFrameRef = useRef(0);

  const rafRef = useRef<number | null>(null);
  const rafRunningRef = useRef(false);
  const idleFramesRef = useRef(0);

  const fallbackImgRef = useRef<HTMLImageElement | null>(null);

  const nearestLeftRef = useRef<Int32Array>(new Int32Array(0));
  const nearestRightRef = useRef<Int32Array>(new Int32Array(0));
  const tablesReadyRef = useRef(false);

  /** Generates the file path for a given frame index */
  const getFileSrc = useMemo(() => {
    return (i: number) => {
      const idx = Number.isFinite(i) ? i | 0 : 0;
      const frameNumber = startFrame + idx;
      const path = `${basePath}/frame_${String(frameNumber).padStart(4, '0')}.webp`;
      if (idx === 0) console.log('First frame path:', path);
      return path;
    };
  }, [basePath, startFrame]);

  const preloader = useFramePreloader({
    total,
    getFileSrc,
  });

  // Debug: log preloader stats
  useEffect(() => {
    const interval = setInterval(() => {
      const stats = preloader.stats();
      console.log('Preloader stats:', stats);
    }, 2000);
    return () => clearInterval(interval);
  }, [preloader]);

  /**
   * Builds lookup tables for nearest loaded frames (left and right)
   * Uses O(n) algorithm to create fast O(1) nearest-neighbor lookups
   */
  const rebuildNearestTables = useCallback(() => {
    const flags = preloader.getLoadedFlags();
    if (!flags || flags.length !== total) return;

    const left = new Int32Array(total);
    const right = new Int32Array(total);

    let last = -1;
    for (let i = 0; i < total; i++) {
      if (flags[i] === 1) last = i;
      left[i] = last;
    }

    last = -1;
    for (let i = total - 1; i >= 0; i--) {
      if (flags[i] === 1) last = i;
      right[i] = last;
    }

    nearestLeftRef.current = left;
    nearestRightRef.current = right;
    tablesReadyRef.current = true;
  }, [preloader, total]);

  /**
   * Finds the nearest loaded frame to the desired frame within a max radius
   * Uses precomputed lookup tables for O(1) performance
   */
  const pickNearestLoaded = useCallback(
    (desired: number, maxRadius = NEARBY_RADIUS) => {
      if (!tablesReadyRef.current) return null;

      const d = clamp(desired | 0, 0, total - 1);
      const left = nearestLeftRef.current;
      const right = nearestRightRef.current;

      const l = left[d];
      const r = right[d];

      if (l === d) return d;

      const dl = l >= 0 ? d - l : 1e9;
      const dr = r >= 0 ? r - d : 1e9;

      const best =
        dl <= dr ? (dl <= maxRadius ? l : -1) : dr <= maxRadius ? r : -1;

      return best >= 0 ? best : null;
    },
    [total]
  );

  /** Resizes canvas to match parent dimensions with device pixel ratio */
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log('Canvas ref not available');
      return;
    }

    const parent = canvas.parentElement;
    if (!parent) {
      console.log('Canvas parent not available');
      return;
    }

    const cssW = parent.clientWidth;
    const cssH = parent.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);

    console.log('Resizing canvas:', cssW, 'x', cssH, 'DPR:', dpr);

    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    ctxRef.current = canvas.getContext('2d', {
      willReadFrequently: false,
    });
  }, []);

  /**
   * Draws image to canvas with proper scaling based on fit mode
   * Handles both 'cover' and 'contain' modes with centered positioning
   */
  const drawImageToCanvas = useCallback(
    (img: HTMLImageElement) => {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !ctx) {
        console.log('Canvas or context not available');
        return;
      }

      const cW = canvas.width;
      const cH = canvas.height;
      console.log('Drawing image to canvas. Canvas size:', cW, 'x', cH, 'Image:', img.src);

      const iW = img.naturalWidth || img.width;
      const iH = img.naturalHeight || img.height;

      const canvasRatio = cW / cH;
      const imgRatio = iW / iH;

      let drawW = cW;
      let drawH = cH;

      if (fit === 'cover') {
        if (imgRatio > canvasRatio) {
          drawH = cH;
          drawW = cH * imgRatio;
        } else {
          drawW = cW;
          drawH = cW / imgRatio;
        }
      } else {
        if (imgRatio > canvasRatio) {
          drawW = cW;
          drawH = cW / imgRatio;
        } else {
          drawH = cH;
          drawW = cH * imgRatio;
        }
      }

      rectRef.current.x = (cW - drawW) / 2;
      rectRef.current.y = (cH - drawH) / 2;
      rectRef.current.w = drawW;
      rectRef.current.h = drawH;

      const prev = ctx.globalCompositeOperation;
      ctx.globalCompositeOperation = 'copy';
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const r = rectRef.current;
      ctx.drawImage(img, r.x, r.y, r.w, r.h);

      ctx.globalCompositeOperation = prev;
    },
    [fit]
  );

  /** Forces a single immediate redraw of the current frame */
  const forceRedrawOnce = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;

    const desired = clamp(
      Math.floor(scrollYProgress.get() * (total - 1)),
      0,
      total - 1
    );

    if (!tablesReadyRef.current) rebuildNearestTables();

    let idx = pickNearestLoaded(desired, FALLBACK_RADIUS);

    if (idx == null) {
      const flags = preloader.getLoadedFlags();
      if (flags && flags.length === total && flags[0] === 1) idx = 0;
    }

    if (idx != null) {
      const img = preloader.getCached(idx);
      if (img) {
        drawImageToCanvas(img);
        currentIdxRef.current = idx;
        lastDrawnRef.current = idx;
      }
    }
  }, [
    drawImageToCanvas,
    pickNearestLoaded,
    preloader,
    scrollYProgress,
    rebuildNearestTables,
    total,
  ]);

  /**
   * Starts the requestAnimationFrame loop for smooth frame updates
   * Implements LERP interpolation for fluid animation
   */
  const startRaf = useCallback(() => {
    if (rafRunningRef.current) return;
    rafRunningRef.current = true;
    idleFramesRef.current = 0;

    if (!fallbackImgRef.current) {
      fallbackImgRef.current = new Image();
      fallbackImgRef.current.decoding = 'async';
    }

    const tick = () => {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;

      if (!canvas || !ctx) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const targetFrame = clamp(
        scrollYProgress.get() * (total - 1),
        0,
        total - 1
      );

      // Lerp (linear interpolation) for smoothing
      smoothFrameRef.current +=
        (targetFrame - smoothFrameRef.current) * LERP_FACTOR;

      const desired = Math.floor(smoothFrameRef.current);
      const current = currentIdxRef.current;

      // Update scroll target for debug overlay
      scrollTargetRef.current = targetFrame;

      if (desired === current) {
        // Keep interpolating even if the frame is the same
        if (Math.abs(targetFrame - smoothFrameRef.current) < LERP_THRESHOLD) {
          idleFramesRef.current += 1;
          if (idleFramesRef.current >= IDLE_FRAMES_THRESHOLD) {
            rafRunningRef.current = false;
            rafRef.current = null;
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      idleFramesRef.current = 0;

      if (!tablesReadyRef.current) rebuildNearestTables();

      let idx = pickNearestLoaded(desired, NEARBY_RADIUS);

      if (idx == null) {
        rebuildNearestTables();
        idx = pickNearestLoaded(desired, FALLBACK_RADIUS);
      }

      if (idx != null && lastDrawnRef.current !== idx) {
        const img = preloader.getCached(idx);
        if (img) {
          drawImageToCanvas(img);
          currentIdxRef.current = idx;
          lastDrawnRef.current = idx;
        }
      } else if (idx == null && process.env.NODE_ENV !== 'production') {
        // Dev-only: load frame directly as fallback
        if (!fallbackImgRef.current) {
          fallbackImgRef.current = new Image();
        }
        const temp = fallbackImgRef.current;
        temp.onload = () => drawImageToCanvas(temp);
        temp.onerror = () => {};
        temp.src = preloader.getOptimizedSrc(desired);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [
    drawImageToCanvas,
    pickNearestLoaded,
    preloader,
    scrollYProgress,
    rebuildNearestTables,
    total,
  ]);

  /** Stops the requestAnimationFrame loop */
  const stopRaf = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    rafRunningRef.current = false;
  }, []);

  useEffect(() => {
    preloader.prime();
    resizeCanvas();
    rebuildNearestTables();

    let raf = 0;
    /** Initial frame drawing function that runs until first frame is loaded */
    const drawFirst = () => {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !ctx) {
        raf = requestAnimationFrame(drawFirst);
        return;
      }

      const desired = clamp(
        Math.floor(scrollYProgress.get() * (total - 1)),
        0,
        total - 1
      );

      if (!tablesReadyRef.current) rebuildNearestTables();

      let idx = pickNearestLoaded(desired, FALLBACK_RADIUS);
      if (idx == null) {
        const flags = preloader.getLoadedFlags();
        if (flags && flags.length === total && flags[0] === 1) idx = 0;
      }

      if (idx != null) {
        const img = preloader.getCached(idx);
        if (img) {
          drawImageToCanvas(img);
          currentIdxRef.current = idx;
          lastDrawnRef.current = idx;
          return;
        }
      }

      raf = requestAnimationFrame(drawFirst);
    };

    raf = requestAnimationFrame(drawFirst);
    return () => cancelAnimationFrame(raf);
  }, [
    drawImageToCanvas,
    pickNearestLoaded,
    preloader,
    rebuildNearestTables,
    resizeCanvas,
    total,
    scrollYProgress,
  ]);

  useEffect(() => {
    let id: number | null = null;

    id = window.setInterval(() => {
      rebuildNearestTables();
      const s = preloader.stats();
      if (s.phase === 'done' && id != null) {
        window.clearInterval(id);
        id = null;
      }
    }, TABLE_REBUILD_INTERVAL);

    return () => {
      if (id != null) window.clearInterval(id);
    };
  }, [preloader, rebuildNearestTables]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    /** Handles canvas resize and triggers redraw */
    const onResize = () => {
      resizeCanvas();
      forceRedrawOnce();
    };

    const ro = new ResizeObserver(onResize);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [forceRedrawOnce, resizeCanvas]);

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    if (!Number.isFinite(latest)) return;

    const raw = latest * (total - 1);

    preloader.hintAround(Math.round(raw), PRELOAD_HINT_RADIUS);

    startRaf();
  });

  useEffect(() => {
    /** Handles document visibility changes to optimize performance */
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        stopRaf();
        return;
      }

      resizeCanvas();
      forceRedrawOnce();
      startRaf();
    };

    /** Handles window focus to resume canvas updates */
    const onFocus = () => {
      resizeCanvas();
      forceRedrawOnce();
      startRaf();
    };

    /** Handles window blur to pause canvas updates */
    const onBlur = () => {
      stopRaf();
    };

    /** Handles page show event to restore canvas state */
    const onPageShow = () => {
      resizeCanvas();
      forceRedrawOnce();
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pageshow', onPageShow);

      stopRaf();

      if (fallbackImgRef.current) {
        fallbackImgRef.current.onload = null;
        fallbackImgRef.current.onerror = null;
        fallbackImgRef.current.src = '';
      }
    };
  }, [forceRedrawOnce, resizeCanvas, startRaf, stopRaf]);

  return (
    <div
      ref={sectionRef}
      style={{ height: `${scrollLengthRem}rem` }}
      className="relative z-[2] w-full lg:w-[187.5rem]"
    >
      <div className="sticky top-[10rem] h-screen w-full overflow-hidden">
        {children}
        <div className="absolute z-[1] h-full w-[140rem] lg:w-full translate-y-[-17rem] lg:translate-y-[-5rem] bg-black/10 translate-x-[-50%] left-1/2">
          <canvas
            ref={canvasRef}
            className="h-full w-full block select-none pointer-events-none"
            style={{ minHeight: '100vh', minWidth: '100%' }}
          />
        </div>
      </div>
    </div>
  );
}
